import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { usePandaDocSyncStatus } from 'core/hooks';

import * as S from './PandaDocAnalytics.styled';

const fmtMoment = (value?: string | null): string =>
  value ? moment(value).format('DD.MM HH:mm') : '';

/**
 * Sticky sync-status pill. Stays pinned in the top-right corner while the user
 * scrolls, so the data freshness is visible page-wide. Always rendered (once
 * the status query resolves) — amber while the details backlog drains, green
 * when everything is synced, red on a sync error.
 */
export const SyncChip = memo(() => {
  const { t } = useTranslation();
  const { status, loading } = usePandaDocSyncStatus();

  if (!status) {
    if (loading) {
      return (
        <S.StickyChip variant="neutral">
          <S.Spinner />
          {t('pages.pandadocAnalytics.sync.checking')}
        </S.StickyChip>
      );
    }
    return null;
  }

  if (status.lastError) {
    return (
      <S.StickyChip variant="error">
        {t('pages.pandadocAnalytics.sync.chipError')}
      </S.StickyChip>
    );
  }

  const pending = status.detailsPending ?? 0;
  if (pending > 0) {
    return (
      <S.StickyChip variant="warn">
        <S.Spinner />
        {t('pages.pandadocAnalytics.sync.chipInProgress', { count: pending })}
      </S.StickyChip>
    );
  }

  const syncedAt = fmtMoment(status.lastDetailsSyncAt || status.lastListSyncAt);
  return (
    <S.StickyChip variant="ok">
      {t('pages.pandadocAnalytics.sync.chipOk')}
      {syncedAt &&
        t('pages.pandadocAnalytics.sync.chipUpdatedAt', { date: syncedAt })}
    </S.StickyChip>
  );
});
